import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { errorMessage } from '@/lib/api/client';
import { Seo } from '@/lib/seo/Seo';
import { useAuth } from '@/features/auth/AuthContext';
import { postLoginPath } from '@/pages/admin/lib/staffPanelPath';
import {
  AUTH_EYE,
  AUTH_FIELD,
  AUTH_LINK,
  AUTH_PRIMARY,
  AuthField,
  AuthShell,
  isStrongPassword,
} from '@/pages/public/auth/AuthShell';

export default function RegisterPage() {
  const { user, loading, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={postLoginPath(user.role, from)} replace />;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (!isStrongPassword(password)) {
      setError('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const next = await signup(name.trim(), email.trim(), password);
      navigate(postLoginPath(next.role, from), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Create account" path="/register" desc="Create a free MAKTech Academy account." />
      <AuthShell
        title="Register"
        backTo="/"
        footer={
          <>
            Already have an account?{' '}
            <Link to="/login" className={AUTH_LINK}>
              Log in
            </Link>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} data-testid="register-form">
          <AuthField label="Full name" htmlFor="register-name">
            <input
              id="register-name"
              className={AUTH_FIELD}
              autoComplete="name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              data-testid="register-name"
            />
          </AuthField>
          <AuthField label="Email" htmlFor="register-email">
            <input
              id="register-email"
              className={AUTH_FIELD}
              type="email"
              autoComplete="email"
              placeholder="username@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="register-email"
            />
          </AuthField>
          <AuthField label="Phone" htmlFor="register-phone">
            <input
              id="register-phone"
              className={AUTH_FIELD}
              type="tel"
              autoComplete="tel"
              placeholder="01X-XXXXXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              data-testid="register-phone"
            />
          </AuthField>
          <AuthField label="Password" htmlFor="register-password">
            <div className="relative">
              <input
                id="register-password"
                className={`${AUTH_FIELD} pr-11`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password"
              />
              <button
                type="button"
                className={AUTH_EYE}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <FiEyeOff className="size-4" aria-hidden /> : <FiEye className="size-4" aria-hidden />}
              </button>
            </div>
            <span className="mt-1.5 block text-xs text-white/65">
              At least 8 characters, with a letter and a number.
            </span>
          </AuthField>
          <AuthField label="Confirm password" htmlFor="register-confirm">
            <div className="relative">
              <input
                id="register-confirm"
                className={`${AUTH_FIELD} pr-11`}
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="register-confirm"
              />
              <button
                type="button"
                className={AUTH_EYE}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                onClick={() => setShowConfirm((v) => !v)}
              >
                {showConfirm ? <FiEyeOff className="size-4" aria-hidden /> : <FiEye className="size-4" aria-hidden />}
              </button>
            </div>
          </AuthField>

          {error ? (
            <p className="mb-3 text-[13.5px] text-[#ffe0d6]" data-testid="register-error">
              {error}
            </p>
          ) : null}

          <button type="submit" className={AUTH_PRIMARY} disabled={busy} data-testid="register-submit">
            {busy ? 'Please wait…' : 'Create free account'}
          </button>
        </form>
      </AuthShell>
    </>
  );
}
